import { Content } from '@prismicio/client'
import { SliceComponentProps } from '@prismicio/react'
import React from 'react'
import styled from '@emotion/styled'
import { TagsListWithLink } from 'library/atoms/Tag'
import { gql, useQuery } from '@apollo/client'
import { Query_Root } from 'shared/presenters/graphqlTypes'
import { ON_SERVER } from 'shared/enums/environment'
import { definedNotNull } from 'shared/presenters/presenter'
import { isFilled } from '@prismicio/client'
import { useRouter } from 'next/router'
/**
 * Props for `LocationTagCloud`.
 */
export type LocationTagCloudProps =
  SliceComponentProps<Content.LocationTagCloudSlice>

/**
 * Component for "LocationTagCloud" Slices.
 */
const LocationTagCloud = ({ slice }: LocationTagCloudProps): JSX.Element => {
  const { query } = useRouter()

  const location = isFilled.keyText(slice.primary.location)
    ? slice.primary.location
    : query.uid

  const { data: lat_long } = useQuery<Pick<Query_Root, 'addresses'>>(
    CURRENT_LAT_LONG,
    {
      variables: {
        q: `%${location}%`
      },
      skip: ON_SERVER,
      ssr: ON_SERVER
    }
  )

  const { data: nearby_locations } = useQuery<
    Pick<Query_Root, 'location_search_schedulables'>
  >(NEARBY_LOCATIONS, {
    variables: {
      lat: lat_long?.addresses?.[0]?.latitude || 0,
      long: lat_long?.addresses?.[0]?.longitude || 0
    },
    skip: ON_SERVER,
    ssr: ON_SERVER
  })

  const nearby_counties = nearby_locations
    ? Array.from(
        new Set(
          nearby_locations.location_search_schedulables
            .map(location => location?.schedulable?.address?.state)
            .filter(state => !!state)
        )
      ).map(state => {
        return {
          label: definedNotNull((state as string).trim()),
          href: definedNotNull(
            `/location/${(state as string).trim().toLowerCase()}`
          )
        }
      })
    : []

  const nearby_cities = nearby_locations
    ? Array.from(
        new Set(
          nearby_locations.location_search_schedulables
            .map(location => location?.schedulable?.address?.city)
            .filter(city => !!city)
        )
      ).map(city => {
        return {
          label: definedNotNull((city as string).trim().toLowerCase()),
          href: definedNotNull(
            `/location/${(city as string).trim().toLowerCase()}`
          )
        }
      })
    : []

  const nearby = [...new Set(nearby_cities.concat(nearby_counties))]

  return (
    <Section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      {nearby && nearby.length > 0 && (
        <>
          <div>
            <TagsListWithLink tags={nearby}></TagsListWithLink>
          </div>

          <br />
        </>
      )}
    </Section>
  )
}

const CURRENT_LAT_LONG = gql`
  query LocationSearch($q: String!) {
    addresses(
      where: { _or: [{ city: { _ilike: $q } }, { state: { _ilike: $q } }] }
      limit: 1
    ) {
      latitude
      longitude
    }
  }
`

// Distnace of 1.5 encompases a radius of approximatly 103 miles.
// The unit is degrees lat/long which is not a perfect circle. but
// 1 degree is approximately 69 miles.
const NEARBY_LOCATIONS = gql`
  query LocationNearbyLocations($lat: float8!, $long: float8!) {
    location_search_schedulables(
      args: { lat: $lat, long: $long }
      where: {
        distance: { _lt: 1.5 }
        schedulable: {
          hide_from_marketplace: { _eq: false }
          materialized_bookability_by_days: { hide_from_public: { _eq: false } }
          outlet: {
            admin_forced_marketplace_disable: { _eq: false }
            feature_toggles: { marketplace_enabled: { _eq: true } }
            widgets: { primary: { _eq: true } }
          }
        }
      }
      limit: 25
    ) {
      schedulable {
        address {
          city
          state
        }
      }
    }
  }
`

export default LocationTagCloud

const Section = styled.section(({ theme }) => ({
  padding: theme.mixins.pxSpread([theme.space[6], theme.space[5]]),
  ...theme.mixins.centeredContainer('xxxl')
}))
